import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { format } from 'date-fns';
import api from '../../utils/api';

const CATEGORY_LABELS = {
  failed_login: 'Failed Login',
  ai_manipulation: 'AI Manipulation',
  foreign_traffic: 'Foreign Traffic',
  other: 'Other'
};

const SOURCE_LABELS = {
  auth: 'Auth',
  supplier: 'Supplier',
  business: 'Business',
  ai_couple: 'AI (Couple)',
  ai_supplier: 'AI (Supplier)',
  other: 'Other'
};

const formatIp = (ip) => {
  if (!ip) return '-';
  if (ip === '::1' || ip === '127.0.0.1' || ip === '::ffff:127.0.0.1') {
    return 'localhost';
  }
  return ip;
};

const SecurityIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState('');

  const fetchIssues = async () => {
    try {
      setError(null);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const response = await api.get('/api/admin/security-issues', { params });
      setIssues(response.data);
    } catch (err) {
      setError('Failed to load security issues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter]);

  const handleUpdateStatus = async (issueId, newStatus) => {
    setUpdating(true);
    try {
      const payload = { status: newStatus };
      if (selectedIssue?._id === issueId && adminNotes !== undefined) {
        payload.adminNotes = adminNotes;
      }
      await api.patch(`/api/admin/security-issues/${issueId}`, payload);
      await fetchIssues();
      setSelectedIssue(null);
      setAdminNotes('');
    } catch (err) {
      setError('Failed to update issue.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'resolved': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'failed_login': return 'error';
      case 'ai_manipulation': return 'warning';
      case 'foreign_traffic': return 'error';
      default: return 'default';
    }
  };

  const handleBanUser = async () => {
    const userId = selectedIssue?.userId?._id || selectedIssue?.userId;
    if (!userId) return;
    setUpdating(true);
    try {
      await api.post(`/api/admin/ban-user/${userId}`, { reason: banReason.trim() || undefined });
      await fetchIssues();
      setBanDialogOpen(false);
      setBanReason('');
      setSelectedIssue(null);
      setAdminNotes('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to ban user.');
    } finally {
      setUpdating(false);
    }
  };

  const canBanUser = selectedIssue && (selectedIssue.userId?._id || selectedIssue.userId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Security Issues
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Failed logins, AI manipulation attempts, and other security events.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="failed_login">Failed Login</MenuItem>
            <MenuItem value="ai_manipulation">AI Manipulation</MenuItem>
            <MenuItem value="foreign_traffic">Foreign Traffic</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>User / Email</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No security issues found.
                </TableCell>
              </TableRow>
            ) : (
              issues.map((issue) => (
                <TableRow key={issue._id}>
                  <TableCell>
                    {format(new Date(issue.createdAt), 'dd MMM yyyy, HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORY_LABELS[issue.category] || issue.category}
                      color={getCategoryColor(issue.category)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{SOURCE_LABELS[issue.source] || issue.source}</TableCell>
                  <TableCell>
                    {issue.userId ? (
                      <>
                        {issue.userId.firstName} {issue.userId.lastName}
                        <Typography variant="caption" display="block">
                          {issue.userId.email}
                        </Typography>
                      </>
                    ) : (
                      issue.email || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {formatIp(issue.ipAddress)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    {issue.messageContent ? (
                      <Typography variant="body2" noWrap title={issue.messageContent}>
                        {issue.messageContent.substring(0, 50)}...
                      </Typography>
                    ) : (
                      issue.metadata?.reason || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={issue.status} color={getStatusColor(issue.status)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedIssue(issue);
                        setAdminNotes(issue.adminNotes || '');
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selectedIssue}
        onClose={() => {
          setSelectedIssue(null);
          setAdminNotes('');
          setBanDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Security Issue</DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Category: {CATEGORY_LABELS[selectedIssue.category] || selectedIssue.category} |
                Source: {SOURCE_LABELS[selectedIssue.source] || selectedIssue.source}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {format(new Date(selectedIssue.createdAt), 'dd MMM yyyy, HH:mm')} | IP: {formatIp(selectedIssue.ipAddress)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                User: {selectedIssue.userId ? `${selectedIssue.userId.firstName} ${selectedIssue.userId.lastName} (${selectedIssue.userId.email})` : selectedIssue.email || '-'}
              </Typography>
              {selectedIssue.messageContent && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Message:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">{selectedIssue.messageContent}</Typography>
                  </Paper>
                </>
              )}
              {selectedIssue.metadata && Object.keys(selectedIssue.metadata).length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Metadata: {JSON.stringify(selectedIssue.metadata)}
                </Typography>
              )}
              <TextField
                label="Admin notes"
                multiline
                rows={2}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                fullWidth
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedIssue(null)}>Close</Button>
          {canBanUser && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => setBanDialogOpen(true)}
              disabled={updating}
            >
              Ban user
            </Button>
          )}
          {selectedIssue?.status === 'pending' && (
            <>
              <Button
                onClick={() => handleUpdateStatus(selectedIssue._id, 'dismissed')}
                disabled={updating}
              >
                Dismiss
              </Button>
              <Button
                onClick={() => handleUpdateStatus(selectedIssue._id, 'reviewed')}
                disabled={updating}
              >
                Mark reviewed
              </Button>
              <Button
                variant="contained"
                onClick={() => handleUpdateStatus(selectedIssue._id, 'resolved')}
                disabled={updating}
              >
                Resolve
              </Button>
            </>
          )}
          {selectedIssue?.status === 'reviewed' && (
            <Button
              variant="contained"
              onClick={() => handleUpdateStatus(selectedIssue._id, 'resolved')}
              disabled={updating}
            >
              Resolve
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={banDialogOpen} onClose={() => !updating && setBanDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ban user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will suspend the user&apos;s account. They will not be able to log in until unbanned.
          </Typography>
          <TextField
            label="Reason for ban (required)"
            multiline
            rows={3}
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            fullWidth
            placeholder="e.g. AI manipulation attempt, repeated failed logins"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBanUser}
            disabled={updating || !banReason.trim()}
          >
            {updating ? 'Banning…' : 'Ban user'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityIssues;
